import React, { useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { usePaystackPayment } from "react-paystack";
import toast from "react-hot-toast";
import { ArrowLeft, Lock, ShoppingBag } from "lucide-react";
import { useCart } from "../../context/CartContext";
import { ordersService } from "../../services/orders";
import { formatPrice } from "../../utils/format";

const ESTIMATED_TAX_RATE = 0.01;
const IBADAN_DELIVERY_FEE = 3000;
const REGIONAL_DELIVERY_FEE = 5000;
const DELIVERY_LOCATIONS = {
  Oyo: [
    "Ibadan",
    "Ogbomoso",
    "Oyo",
    "Iseyin",
    "Saki",
    "Eruwa",
    "Fiditi",
    "Igboho",
    "Kisi",
    "Igbo Ora",
    "Ago Are",
    "Moniya",
    "Apata",
    "Challenge",
    "Bodija",
    "Mokola",
    "Akobo",
    "Oluyole",
    "Ring Road",
    "Eleyele",
    "Ojoo",
    "Akinyele",
    "Lanlate",
    "Ido",
    "Ona Ara",
    "Egbeda",
    "Lalupon",
    "Egbin",
    "Tede",
    "Sepeteri",
    "Igbeti",
    "Jobele",
    "Otu",
    "Ilora",
    "Ojongbodu",
    "Awe",
    "Olorunsogo",
    "Ogbomosho North",
    "Ogbomosho South",
    "Irepo",
    "Orelope",
    "Atisbo",
    "Kajola",
    "Ibarapa",
  ],
  Lagos: [
    "Ikeja",
    "Lagos Island",
    "Lekki",
    "Ajah",
    "Ikorodu",
    "Epe",
    "Badagry",
    "Yaba",
    "Surulere",
    "Festac",
    "Victoria Island",
    "Ikoyi",
    "Maryland",
    "Magodo",
    "Ogudu",
    "Gbagada",
    "Ketu",
    "Mile 12",
    "Ojota",
    "Alimosho",
    "Agege",
    "Ifako-Ijaiye",
    "Egbeda",
    "Ipaja",
    "Abule Egba",
    "Akowonjo",
    "Isheri",
    "Mushin",
    "Oshodi",
    "Isolo",
    "Okota",
    "Amuwo Odofin",
    "Apapa",
    "Ilupeju",
    "Anthony",
    "Chevron",
    "Sangotedo",
    "Awoyaya",
    "Ibeju-Lekki",
    "Ajegunle",
    "Ikate",
    "Oniru",
    "Ikotun",
    "LASU Road",
    "Idimu",
    "Lakowe",
  ],
  Ogun: [
    "Abeokuta",
    "Ijebu Ode",
    "Sagamu",
    "Ota",
    "Ilaro",
    "Ifo",
    "Ago Iwoye",
    "Shagamu",
    "Ayetoro",
    "Owode",
    "Mowe",
    "Ibafo",
    "Sango Ota",
    "Atan",
    "Agbara",
    "Ijoko",
    "Ado-Odo",
    "Remo",
    "Ijebu Igbo",
    "Ikenne",
    "Ilisan",
    "Ofada",
    "Wasimi",
    "Odeda",
    "Imeko",
    "Ipokia",
    "Ayetoro Gateway",
    "Iperu",
    "Ogere",
    "Awa",
    "Ilara",
    "Ilishan",
    "Ado-Odo/Ota",
    "Obafemi Owode",
    "Ewekoro",
    "Ijebu North",
    "Ijebu East",
    "Yewa North",
    "Yewa South",
  ],
  Osun: [
    "Osogbo",
    "Ile-Ife",
    "Ilesa",
    "Ede",
    "Ikirun",
    "Ejigbo",
    "Iwo",
    "Iragbiji",
    "Gbongan",
    "Ikire",
    "Modakeke",
    "Ikirun Road",
    "Ipetumodu",
    "Apomu",
    "Otan Ayegbaju",
    "Ila Orangun",
    "Ejigbo Road",
    "Esa Oke",
    "Ede North",
    "Ede South",
    "Ada",
    "Oyan",
    "Ilobu",
    "Erin-Osun",
    "Okuku",
    "Ororuwo",
    "Bode Osi",
    "Owena",
    "Ipetu Ijesa",
    "Ife Central",
    "Ife East",
    "Ife North",
    "Ilesa East",
    "Ilesa West",
    "Boripe",
    "Boluwaduro",
    "Ayedire",
    "Atakunmosa",
    "Obokun",
  ],
};
const DELIVERY_COVERAGE = Object.keys(DELIVERY_LOCATIONS);

const TRUST_BADGES = [
  "100% Authentic Products",
  "Secure Checkout",
  "Fast South-West Delivery",
  "Easy Returns",
];

const FALLBACK = "https://images.unsplash.com/photo-1590736969955-71cc94901144?w=200&q=80";

const FORM_FIELDS = [
  { field: "name", label: "Full Name", type: "text", placeholder: "Jane Doe" },
  { field: "email", label: "Email Address", type: "email", placeholder: "jane@example.com" },
  { field: "phone", label: "Phone Number", type: "tel", placeholder: "+234 800 000 0000" },
  { field: "address", label: "Delivery Address", type: "text", placeholder: "House number, street, area" },
];

function OrderItem({ item }) {
  return (
    <div className="flex items-center gap-3 py-3 border-b border-gray-100 last:border-0">
      <div className="w-14 h-16 rounded-sm overflow-hidden bg-brand-cream shrink-0">
        <img
          src={item.image_url || FALLBACK}
          alt={item.name}
          className="w-full h-full object-cover"
          loading="eager"
          fetchPriority="high"
          onError={(event) => { event.currentTarget.src = FALLBACK; }}
        />
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-display text-base text-brand-charcoal truncate">{item.name}</p>
        <p className="font-body text-xs text-gray-400 mt-0.5">Qty: {item.quantity}</p>
      </div>
      <p className="font-body text-sm font-semibold text-brand-charcoal shrink-0">
        {formatPrice(item.price * item.quantity)}
      </p>
    </div>
  );
}

function Spinner() {
  return (
    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
    </svg>
  );
}

function getDeliveryFee(state, city) {
  if (!state || !city) return 0;
  if (state === "Oyo") return IBADAN_DELIVERY_FEE;
  return REGIONAL_DELIVERY_FEE;
}

function roundMoney(value) {
  return Math.round((value + Number.EPSILON) * 100) / 100;
}

export default function CheckoutForm() {
  const { items, totalAmount: subtotalAmount, clearCart } = useCart();
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    state: "",
    city: "",
    address: "",
  });
  const paymentRef = useRef(`TW_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`);
  const paystackKey = process.env.REACT_APP_PAYSTACK_PUBLIC_KEY;
  const isPaymentConfigured = Boolean(paystackKey && !paystackKey.includes("placeholder"));
  const hasSelectedLocation = Boolean(form.state && form.city);
  const availableCities = useMemo(() => (form.state ? DELIVERY_LOCATIONS[form.state] || [] : []), [form.state]);
  const deliveryFee = useMemo(
    () => (items.length > 0 ? getDeliveryFee(form.state, form.city) : 0),
    [items.length, form.state, form.city]
  );
  const taxableAmount = useMemo(() => roundMoney(subtotalAmount), [subtotalAmount]);
  const estimatedTaxAmount = useMemo(
    () => roundMoney(taxableAmount * ESTIMATED_TAX_RATE),
    [taxableAmount]
  );
  const totalAmount = useMemo(
    () => roundMoney(subtotalAmount + deliveryFee + estimatedTaxAmount),
    [subtotalAmount, deliveryFee, estimatedTaxAmount]
  );
  const summaryTotalLabel = hasSelectedLocation ? formatPrice(totalAmount) : "Select location";
  const isCheckoutDisabled = saving || !isPaymentConfigured || !hasSelectedLocation || items.length === 0;

  const set = (field) => (event) => {
    const value = event.target.value;
    setForm((previous) => {
      if (field === "state") {
        return { ...previous, state: value, city: "" };
      }
      return { ...previous, [field]: value };
    });
  };

  const paystackConfig = {
    reference: paymentRef.current,
    email: form.email || "guest@tiwani.com",
    amount: Math.round(totalAmount * 100),
    publicKey: paystackKey || "pk_test_placeholder",
    currency: "NGN",
    metadata: {
      custom_fields: [
        { display_name: "Customer Name", variable_name: "customer_name", value: form.name },
        { display_name: "Phone", variable_name: "phone", value: form.phone },
        { display_name: "State", variable_name: "state", value: form.state },
        { display_name: "City", variable_name: "city", value: form.city },
        { display_name: "Delivery Address", variable_name: "address", value: form.address },
      ],
    },
  };

  const onPaystackSuccess = async (reference) => {
    setSaving(true);
    try {
      const orderItems = items.map((item) => ({
        id: item.id,
        name: item.name,
        price: item.price,
        quantity: item.quantity,
        image_url: item.image_url || null,
        category: item.category || null,
      }));

      const result = await ordersService.verifyPaymentAndCreateOrder({
        customerName: form.name,
        email: form.email,
        phone: form.phone,
        address: `${form.address} (${form.city}, ${form.state})`,
        items: orderItems,
        totalAmount,
        paymentRef: reference.reference,
      });

      toast.success(result?.message || "Payment verified and order saved.");
      clearCart();
      navigate("/order-success", { state: { reference: reference.reference, customerName: form.name } });
    } catch (error) {
      console.error("Order verification failed:", error);
      toast.error(error.message || `Payment received (${reference.reference}) but verification failed. Please contact support.`);
    } finally {
      setSaving(false);
    }
  };

  const onPaystackClose = () => toast("Payment cancelled.", { icon: "i" });

  const initializePayment = usePaystackPayment(paystackConfig);

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!form.name.trim()) return toast.error("Please enter your full name.");
    if (!form.email.trim()) return toast.error("Please enter your email address.");
    if (!form.phone.trim()) return toast.error("Please enter your phone number.");
    if (!form.state) return toast.error("Please select your state.");
    if (!form.city) return toast.error("Please select your city.");
    if (!form.address.trim()) return toast.error("Please enter your delivery address.");
    if (items.length === 0) return toast.error("Your cart is empty.");
    if (!isPaymentConfigured) {
      return toast.error("Payment is not configured yet. Please contact support.");
    }
    if (!hasSelectedLocation) {
      return toast.error("Please select a delivery location before checkout.");
    }
    initializePayment({ onSuccess: onPaystackSuccess, onClose: onPaystackClose });
  };

  if (items.length === 0 && !saving) {
    return (
      <div className="min-h-screen bg-brand-cream flex items-center justify-center px-4">
        <div className="text-center">
          <ShoppingBag size={56} className="text-gray-300 mx-auto mb-5" />
          <h2 className="font-display text-3xl text-brand-dark mb-3">Your cart is empty</h2>
          <p className="font-body text-gray-400 mb-8">Add some fragrances before checking out.</p>
          <Link to="/products" className="btn-primary">Browse Collection</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-brand-cream pt-28 pb-20 px-4">
      <div className="max-w-5xl mx-auto">
        <Link to="/products" className="inline-flex items-center gap-2 font-body text-sm text-gray-400 hover:text-brand-red transition-colors mb-8">
          <ArrowLeft size={14} /> Continue Shopping
        </Link>

        <div className="mb-10">
          <span className="section-label">Almost There</span>
          <h1 className="font-display text-5xl text-brand-dark font-light mt-2">Checkout</h1>
        </div>

        <div className="grid lg:grid-cols-[1fr_400px] gap-8 items-start">
          <form onSubmit={handleSubmit} className="bg-white p-8 rounded-sm shadow-sm">
            <h2 className="font-display text-2xl text-brand-dark mb-7">Delivery Details</h2>
            <div className="flex flex-col gap-5">
              {FORM_FIELDS.map(({ field, label, type, placeholder }) => (
                <div key={field}>
                  <label className="block font-body text-[11px] uppercase tracking-widest text-gray-400 mb-2">
                    {label} <span className="text-brand-red">*</span>
                  </label>
                  <input
                    type={type}
                    value={form[field]}
                    onChange={set(field)}
                    placeholder={placeholder}
                    required
                    className="input-field"
                  />
                </div>
              ))}

              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className="block font-body text-[11px] uppercase tracking-widest text-gray-400 mb-2">
                    State <span className="text-brand-red">*</span>
                  </label>
                  <select
                    value={form.state}
                    onChange={set("state")}
                    required
                    className="input-field"
                  >
                    <option value="">Select state</option>
                    {DELIVERY_COVERAGE.map((state) => (
                      <option key={state} value={state}>
                        {state}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block font-body text-[11px] uppercase tracking-widest text-gray-400 mb-2">
                    City <span className="text-brand-red">*</span>
                  </label>
                  <select
                    value={form.city}
                    onChange={set("city")}
                    required
                    disabled={!form.state}
                    className="input-field disabled:bg-gray-100 disabled:text-gray-400"
                  >
                    <option value="">{form.state ? "Select city" : "Select state first"}</option>
                    {availableCities.map((city) => (
                      <option key={city} value={city}>
                        {city}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2 mt-6 text-gray-400">
              <Lock size={12} />
              <p className="font-body text-[11px]">Your payment is securely processed, and we never store your card details.</p>
            </div>

            <div className="mt-6 rounded-sm border border-gray-100 bg-brand-cream/40 px-4 py-4 space-y-2">
              <p className="font-body text-sm text-brand-charcoal">Delivery: 1-2 days Oyo, 3-5 days nationwide.</p>
            </div>

            <button
              type="submit"
              disabled={isCheckoutDisabled}
              className="mt-6 w-full py-4 bg-brand-red text-white font-body font-semibold tracking-wide text-sm rounded-sm hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {saving ? <><Spinner />Verifying payment...</> : "Complete Payment"}
            </button>
            <div className="mt-4 flex items-center gap-2 text-brand-charcoal">
              <Lock size={13} />
              <p className="font-body text-xs">Secure Checkout</p>
            </div>
            <p className="mt-3 font-body text-xs text-gray-500 leading-relaxed">
              By ordering, you agree to{" "}
              <Link to="/shipping-policy" className="text-brand-red hover:text-red-700 transition-colors">
                Shipping
              </Link>{" "}
              &{" "}
              <Link to="/refund-policy" className="text-brand-red hover:text-red-700 transition-colors">
                Return
              </Link>{" "}
              policies.
            </p>
            {!hasSelectedLocation && (
              <p className="mt-3 font-body text-xs text-amber-700">
                Select your delivery state and city to continue.
              </p>
            )}
            {!isPaymentConfigured && (
              <p className="mt-3 font-body text-xs text-amber-700">
                Checkout is temporarily unavailable until payment setup is completed.
              </p>
            )}
          </form>

          <div className="flex flex-col gap-5">
            <div className="bg-white p-6 rounded-sm shadow-sm">
              <h2 className="font-display text-2xl text-brand-dark mb-4">Order Summary</h2>
              {items.map((item) => <OrderItem key={item.id} item={item} />)}
              <div className="mt-5 pt-5 border-t border-gray-100 space-y-3">
                <div className="flex justify-between text-sm font-body items-center">
                  <span className="text-gray-500">Subtotal</span>
                  <span className="text-brand-charcoal">{formatPrice(subtotalAmount)}</span>
                </div>
                <div className="flex justify-between text-sm font-body items-center">
                  <span className="text-gray-500">Delivery</span>
                  <span className="text-brand-charcoal">
                    {hasSelectedLocation ? formatPrice(deliveryFee) : "Select location"}
                  </span>
                </div>
                <div className="flex justify-between text-sm font-body items-center">
                  <span className="text-gray-500">Estimated Tax</span>
                  <span className="text-brand-charcoal">{formatPrice(estimatedTaxAmount)}</span>
                </div>
              </div>
              <div className="mt-5 rounded-sm bg-brand-cream/45 px-4 py-4">
                <div className="flex justify-between items-center gap-4">
                  <span className="font-body text-sm uppercase tracking-widest text-brand-charcoal">Total</span>
                  <span className="font-display text-3xl text-brand-red font-semibold">{summaryTotalLabel}</span>
                </div>
              </div>
              <p className="mt-4 font-body text-xs text-gray-400">
                Estimated tax is calculated on goods only, and updates automatically with your cart.
              </p>
            </div>

            <div className="bg-white p-6 rounded-sm shadow-sm">
              <p className="font-body text-[10px] uppercase tracking-widest text-gray-400 mb-4 text-center">Why shop with Tiwani</p>
              {TRUST_BADGES.map((badge) => (
                <div key={badge} className="flex items-center gap-2.5 py-1.5">
                  <div className="w-1.5 h-1.5 rounded-full bg-brand-red shrink-0" />
                  <span className="font-body text-sm text-gray-600">{badge}</span>
                </div>
              ))}
              <div className="mt-4 pt-4 border-t border-gray-100">
                <p className="font-body text-xs text-gray-500 text-center">
                  Delivery currently covers {DELIVERY_COVERAGE.join(", ")}.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
